import React, { useEffect, useState } from 'react';
import {
  DocumentReferenceAttachment,
  PAGE_NUMBER_EXTENSION_URL,
  ReferenceOrResource,
} from '../Attachment';
import { useDocumentReference } from '../../../../../../hooks/useDocumentReference';
import { defineMessages, useIntl } from 'react-intl';
import { useNotify } from '../../../../../../hooks';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { useModalStyles } from './styles';
import { CsvAttachmentViewer } from '../../Attachments/CsvAttachmentViewer';
import {
  hasCsvExtension,
  isCsvMime,
} from '../../../../../../util/knowledgeBaseCitation';

const messages = defineMessages({
  viewPDFError: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.documentReferenceModal.viewPDFError',
    defaultMessage:
      'Error loading attachment. Check that you are using All Cohorts and try refreshing the page.',
  },
});

type DocumentReferenceModalProps = {
  attachment: DocumentReferenceAttachment;
  reference?: ReferenceOrResource;
  onDismiss: () => void;
  disableDeepLink?: boolean;
  disablePdfNav?: boolean;
  openInNewTab?: boolean;
};

export const DocumentReferenceContent = (
  props: Omit<DocumentReferenceModalProps, 'onDismiss'> & {
    onDismiss?: () => void;
  },
) => {
  const { attachment, reference, disableDeepLink, onDismiss } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = React.useState<string>();
  const isMountedRef = React.useRef(false);
  const { retrieveDocumentReference, retrieveAttachment } =
    useDocumentReference();

  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const classes = useModalStyles();

  // CSVs render in-app as a sortable table, so they intentionally bypass the
  // `openInNewTab` short-circuit (browsers download CSVs from new tabs) and
  // the iframe path (same reason). They also skip the PDF page-anchor since
  // it would no-op on a non-PDF URL.
  const initiallyKnownCsv = attachment.isCsv();
  const shouldRenderCsv =
    initiallyKnownCsv ||
    Boolean(isFetched && attachmentUrl && !attachmentUrl.includes('#page='));

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isLoading || isFetched) return;

    setIsLoading(true);

    const id = attachment.resource.id;
    if (!id) {
      setIsLoading(false);
      return;
    }

    const loadDocument = async (retriesLeft = 5) => {
      try {
        const docRef = await retrieveDocumentReference(id);
        const attachmentData = await retrieveAttachment(docRef);
        if (!isMountedRef.current) {
          return;
        }
        const fetchedCsv =
          initiallyKnownCsv ||
          new DocumentReferenceAttachment(docRef).isCsv() ||
          isCsvMime(attachmentData?.contentType) ||
          hasCsvExtension(attachmentData?.name) ||
          hasCsvExtension(attachmentData?.downloadUrl);
        const pageNumber = disableDeepLink
          ? 1 // If deep linking is disabled, always show the first page
          : (reference as fhir.DocumentReference)?.extension?.find(
              ext => ext.url === PAGE_NUMBER_EXTENSION_URL,
            )?.valueInteger ?? 1;

        if (fetchedCsv) {
          // No `#page=` anchor: CSVs aren't paged and the fragment would
          // confuse the file-service signed URL parser.
          setAttachmentUrl(attachmentData?.downloadUrl);
          return;
        }

        const url = `${attachmentData?.downloadUrl}#page=${pageNumber}${
          props.disablePdfNav ? '&navpanes=0' : ''
        }`;
        setAttachmentUrl(url);

        if (props.openInNewTab) {
          window.open(url, '_blank', 'noopener noreferrer');
          onDismiss?.();
        }
      } catch (error) {
        if (error.response.status === 404) {
          // retry on a 404 after a second,
          // this fixes an occasional race where the new reference document is not yet available after uploading
          if (retriesLeft > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (!isMountedRef.current) {
              return;
            }
            await loadDocument(retriesLeft - 1);
            return;
          }
        }

        if (!isMountedRef.current) {
          return;
        }
        notify(formatMessage(messages.viewPDFError), 'error');
        onDismiss?.();
      }
    };

    loadDocument().finally(() => {
      if (!isMountedRef.current) {
        return;
      }
      setIsFetched(true);
      setIsLoading(false);
    });
  }, [
    attachment,
    reference,
    isLoading,
    isFetched,
    initiallyKnownCsv,
    disableDeepLink,
    props.openInNewTab,
    notify,
    retrieveAttachment,
    retrieveDocumentReference,
    formatMessage,
    onDismiss,
    props.disablePdfNav,
  ]);

  if (shouldRenderCsv) {
    if (isLoading || !attachmentUrl) {
      return (
        <Box fullWidth fullHeight justify="center" align="center">
          <DotLoader size={1} />
        </Box>
      );
    }
    // In the modal flow (`openInNewTab` is forced true by AttachmentArbiter),
    // surface the CSV viewer's own header so the user gets the filename and
    // a Download affordance — the chroma Modal chrome around us is otherwise
    // titleless. In the inline-expanded flow the parent ExpansionPanel
    // already provides those controls, so we hide the duplicate header.
    return (
      <CsvAttachmentViewer
        url={attachmentUrl}
        downloadUrl={attachmentUrl}
        fileName={attachment.rawDisplay ?? attachment.identifier}
        hideHeader={!props.openInNewTab}
        data-testid="document-reference-csv"
      />
    );
  }

  if (props.openInNewTab) {
    return null;
  }

  return (
    <Box fullHeight fullWidth align="center" justify="center">
      {isLoading ? (
        <Box fullWidth fullHeight justify="center" align="center">
          <DotLoader size={1} />
        </Box>
      ) : attachment.isImage() ? (
        <img
          className={classes.img}
          src={attachmentUrl}
          alt={attachment.identifier}
        />
      ) : (
        <iframe
          title={attachment.identifier}
          className={classes.iframe}
          frameBorder="0"
          src={attachmentUrl}
        />
      )}
    </Box>
  );
};

export const DocumentReferenceModal = (props: DocumentReferenceModalProps) => {
  const classes = useModalStyles({ thumbnail: false });
  return (
    <Modal
      isFullScreen
      isOpen={true}
      onDismiss={props.onDismiss}
      contentClassName={classes.modalContent}
    >
      <DocumentReferenceContent {...props} />
    </Modal>
  );
};
